import {
  Flex,
  HStack,
  Menu,
  Separator,
  Text,
  useBreakpointValue
} from "@chakra-ui/react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { LearningClassEnums as CLS } from "@/ts/enums/LearningClassEnums";
import {
  AvatarImage,
  ColorModeButton,
  GradeTheme,
  Logo,
  NavigationIcons,
  getStudentLogo,
  getTrackingId,
  type ModalRef
} from "@euka-mfes/bark";
import ImpersonationBanner from "@components/ImpersonationBanner";

import { useStudentData } from "@/context";
import type { Grade } from "@/ts/interfaces/student/studentAccountInterface";
import primaryDarkBgImage from "@assets/header-primary-dark.png";
import primaryLightBgImage from "@assets/header-primary-light.png";
import secondaryBgImage from "@assets/header-secondary.jpg";
import seniorBgImage from "@assets/header-senior.jpg";
import LessonHeaderBreadcrumb from "@components/lessons/LessonHeaderBreadcrumb";
import SelectGradeModal from "@components/SelectGradeModal";
import PinInputModal from "@components/student/PinInputModal";
import useCrossTabLessonSync from "@hooks/useCrossTabLessonSync";
import { GRADE_STATUS } from "@ts/enums/GradeStatus";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import { getCurrentSectionFromPath } from "@utils/lessonUtils";
import { logger, setDatadogUserContext } from "@utils/loggingUtils";
import { getAccountRoute, getBaseRoute, getHomeRoute } from "@utils/route";
import {
  getOrientationStartUrl,
  shouldRedirectToOrientation
} from "@/utils/orientation";
import {
  ensureGradeInPath,
  mapGradeCodeToGradeParamId,
  replaceGradeInPath
} from "@utils/routeUtil";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent
} from "react";

const HEADER_HEIGHT_VAR = "--euka-header-height";

interface HeaderProps {
  showBreadcrumb?: boolean;
  isOrientation?: boolean;
}

const getHeaderStyles = (): Record<
  GradeTheme,
  {
    color: string;
    backgroundImageLight: string;
    backgroundImageDark: string;
    backgroundPosition: string;
    backgroundRepeat: string;
    backgroundSize: string;
    backgroundOrigin: string;
    borderBottom: string;
    borderBottomStyle: string;
    borderBottomColor: string;
    textColor: string;
    headerHeight: { base: string; md: string } | string;
    iconColor: string;
    separatorColor: string;
    themeSwitchColor: string;
  }
> => ({
  PRIMARY: {
    color: "gradeTheme.headerBg",
    backgroundImageLight: `url(${primaryLightBgImage})`,
    backgroundImageDark: `url(${primaryDarkBgImage})`,
    backgroundPosition: "bottom left",
    backgroundRepeat: "no-repeat",
    backgroundSize: "100% auto",
    backgroundOrigin: "padding-box",
    borderBottom: "0px",
    borderBottomStyle: "solid",
    borderBottomColor: "gradeTheme.headerDivider",
    textColor: "gradeTheme.headerText",
    headerHeight: "96px",
    iconColor: "gradeTheme.iconColor",
    separatorColor: "gradeTheme.iconColor",
    themeSwitchColor: "gradeTheme.themeSwitchColor"
  },
  SECONDARY: {
    color: "gradeTheme.headerBg",
    backgroundImageLight: `url(${secondaryBgImage})`,
    backgroundImageDark: `url(${secondaryBgImage})`,
    backgroundPosition: "top left",
    backgroundRepeat: "repeat-x",
    backgroundSize: "none",
    backgroundOrigin: "padding-box",
    borderBottom: "0px",
    borderBottomStyle: "solid",
    borderBottomColor: "gradeTheme.headerDivider",
    textColor: "gradeTheme.headerText",
    headerHeight: "96px",
    iconColor: "gradeTheme.iconColor",
    separatorColor: "gradeTheme.iconColor",
    themeSwitchColor: "gradeTheme.themeSwitchColor"
  },
  SENIOR: {
    color: "gradeTheme.headerBg",
    backgroundImageLight: `url(${seniorBgImage})`,
    backgroundImageDark: `url(${seniorBgImage})`,
    backgroundPosition: "top left",
    backgroundRepeat: "repeat-x",
    backgroundSize: "none",
    backgroundOrigin: "padding-box",
    borderBottom: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: "gradeTheme.headerDivider",
    textColor: "gradeTheme.headerText",
    headerHeight: "96px",
    iconColor: "gradeTheme.iconColor",
    separatorColor: "gradeTheme.iconColor",
    themeSwitchColor: "gradeTheme.themeSwitchColor"
  }
});

const Header = ({
  showBreadcrumb = false,
  isOrientation = false
}: HeaderProps) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const headerStyles = getHeaderStyles();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const base = getBaseRoute();

  // Get everything from context
  const {
    headerDetails,
    headerDetailsStatus,
    selectedGrade,
    gradeTheme,
    changedGrade,
    setChangedGrade,
    setActivatingGradeCode,
    hasAttemptedApplyQuestion,
    setHasAttemptedApplyQuestion,
    isApplyResultViewShown,
    setShowBlockNavigationModal,
    setPendingNavigationPath,
    fetchGradeChangeOtp,
    validateGradeChangeOtp,
    fetchHeaderDetails,
    getGradeChangeOtpRequestStatus,
    validateGradeChangeOtpRequestStatus,
    resetTermsDetails
  } = useStudentData();

  const currentSection = getCurrentSectionFromPath(pathname);

  // Cross-tab sync
  useCrossTabLessonSync();

  const {
    color,
    backgroundImageLight,
    backgroundImageDark,
    backgroundRepeat,
    backgroundSize,
    backgroundPosition,
    borderBottom,
    borderBottomStyle,
    borderBottomColor,
    textColor,
    headerHeight,
    iconColor,
    separatorColor
  } = headerStyles[gradeTheme];

  const gradeSelectModalRef = useRef<ModalRef | null>(null);
  const otpModalRef = useRef<ModalRef | null>(null);
  const closingForOtpRef = useRef(false);
  const closingForValidateOtpRef = useRef(false);
  const headerContainerRef = useRef<HTMLDivElement | null>(null);

  const [pinCodes, setPinCodes] = useState<string[]>([]);
  const [validateOTPError, setValidateOTPError] = useState("");
  const [getOTPError, setGetOTPError] = useState("");
  const [targetGrade, setTargetGrade] = useState<Grade | null>(null);

  const handleAccountClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (currentSection === "apply") {
      event.preventDefault();

      if (hasAttemptedApplyQuestion && !isApplyResultViewShown) {
        setPendingNavigationPath(
          getAccountRoute(selectedGrade?.grade_code as number)
        );
        setShowBlockNavigationModal(true);
      } else {
        navigate(getAccountRoute(selectedGrade?.grade_code as number));
        setHasAttemptedApplyQuestion(false);
      }
    }
  };

  const updateUrlGradeParam = (newGrade: Grade) => {
    const newGradeParam = mapGradeCodeToGradeParamId(
      newGrade.grade_code
    ) as string;

    // Skip URL grade updates on orientation routes (grade selector is hidden)
    if (pathname.includes("/orientation")) {
      return;
    }

    // When on any assignments route, land on the dashboard for the new grade
    // rather than preserving the assignments path (assignments are grade-specific)
    if (pathname.includes("/assignments")) {
      navigate(`${base}${newGradeParam}`, { replace: true });
      return;
    }

    const next = pathname.startsWith(base)
      ? replaceGradeInPath(pathname, newGradeParam)
      : ensureGradeInPath(pathname, newGradeParam);

    if (next !== pathname) navigate(next, { replace: true });
  };

  const onPinVerification = async () => {
    setValidateOTPError("");

    const pin = pinCodes.join("");

    try {
      await validateGradeChangeOtp(pin);

      // Dismiss only after successful validation
      closingForValidateOtpRef.current = true;
      otpModalRef.current?.dismiss();

      resetTermsDetails();

      if (changedGrade) {
        // Refresh header details to get updated grade status
        await fetchHeaderDetails();
        updateUrlGradeParam(changedGrade);
      }
      setPinCodes(["", "", "", ""]);
      setChangedGrade(null);
      setActivatingGradeCode(null);
    } catch (error: unknown) {
      const err = error as {
        code?: number;
        customAttributes?: { httpErrorStatus?: number };
        message?: string;
      };
      if (
        err?.customAttributes?.httpErrorStatus === 403 ||
        err?.code === 403
      ) {
        setValidateOTPError(
          "Sorry, that code is incorrect, or has expired. Please try again, or request a new code."
        );
      } else {
        setValidateOTPError(err?.message ?? "Validation failed");
      }

      logger.error("VALIDATE OTP API ERROR", error);
    }
  };

  const handleRequestNewCode = async () => {
    setValidateOTPError("");
    setPinCodes(["", "", "", ""]);

    try {
      await fetchGradeChangeOtp(targetGrade?.grade_code as number);
    } catch (error: unknown) {
      const err = error as { message?: string };
      setValidateOTPError(err?.message ?? "Failed to get new code");
      logger.error("GET OTP API ERROR", error);
    }
  };

  const handlePinCancel = () => {
    setValidateOTPError("");
    setPinCodes(["", "", "", ""]);
    setTargetGrade(null);
    setActivatingGradeCode(null);
    setChangedGrade(null);
  };

  const getOtp = async (grade: Grade) => {
    setGetOTPError("");
    setTargetGrade(grade);
    setActivatingGradeCode(grade.grade_code);

    try {
      await fetchGradeChangeOtp(grade?.grade_code);
      closingForOtpRef.current = true;
      gradeSelectModalRef.current?.dismiss();
      otpModalRef.current?.show();
    } catch (error: unknown) {
      const err = error as { message?: string };
      setGetOTPError(err?.message ?? "Failed to get OTP");
      logger.error("GET OTP API ERROR", error);
    }
  };

  const handleGradeSelect = (selectedNewGrade: Grade) => {
    setChangedGrade(selectedNewGrade);

    if (
      selectedNewGrade.status === GRADE_STATUS.CURRENT ||
      selectedNewGrade.status === GRADE_STATUS.ACTIVE
    ) {
      // For active grades, just navigate - context will handle data refresh
      gradeSelectModalRef.current?.dismiss();
      setTargetGrade(null);
      setChangedGrade(null);
      updateUrlGradeParam(selectedNewGrade);
      return;
    }

    if (selectedNewGrade.status === GRADE_STATUS.NOT_STARTED)
      getOtp(selectedNewGrade);
  };

  const handleGradeChangeCancel = () => {
    setGetOTPError("");
    setChangedGrade(null);
  };

  useEffect(() => {
    if (!selectedGrade ||
        pathname.includes("/orientation") ||
        !shouldRedirectToOrientation({
            orientationEnabled: headerDetails.orientationEnabled,
            grade: selectedGrade,
        })
    )
      return;

    logger.log(
      "Redirecting to orientation for grade",
      selectedGrade.grade_code
    );

    navigate(
      getOrientationStartUrl(
        selectedGrade.grade_code,
        headerDetails.userState?.introScreenSeen
      ),
      {
        replace: true
      }
    );
  }, [
    selectedGrade?.grade_code,
    selectedGrade?.orientation_completed,
    headerDetails.orientationEnabled,
    headerDetails.userState?.introScreenSeen,
    pathname,
    navigate
  ]);

  useEffect(() => {
    const studentUser = headerDetails.student;
    if (studentUser) {
      setDatadogUserContext({
        id: studentUser.username,
        eukaId: studentUser.username,
        role: "student"
      });
    }
  }, [headerDetails.student]);

  const syncHeaderHeight = useCallback(() => {
    const node = headerContainerRef.current;
    if (!node) return;

    document.documentElement.style.setProperty(
      HEADER_HEIGHT_VAR,
      `${node.offsetHeight}px`
    );
  }, []);

  // Publish the full header height (including impersonation banner and
  // decorative wave) as a CSS variable so layout consumers can reference
  // it without hardcoded values.
  useLayoutEffect(() => {
    syncHeaderHeight();

    const node = headerContainerRef.current;
    if (!node) return;

    const rafId = window.requestAnimationFrame(syncHeaderHeight);
    const timeoutId = window.setTimeout(syncHeaderHeight, 0);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(syncHeaderHeight)
        : null;
    resizeObserver?.observe(node);

    const mutationObserver = new MutationObserver(syncHeaderHeight);
    mutationObserver.observe(node, {
      childList: true,
      subtree: true,
      characterData: true
    });

    window.addEventListener("resize", syncHeaderHeight);
    window.visualViewport?.addEventListener("resize", syncHeaderHeight);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", syncHeaderHeight);
      window.visualViewport?.removeEventListener("resize", syncHeaderHeight);
    };
  }, [syncHeaderHeight]);

  return (
    <Flex
      ref={headerContainerRef}
      className="app-header"
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex="modal"
      w="100%"
      minW="100%"
      direction="column"
    >
      <ImpersonationBanner />
      <Flex
        data-testid="header-row"
        direction="row"
        alignItems="center"
        justify="space-between"
        alignContent="center"
        minW="100%"
        w="100%"
        paddingLeft={{ base: 4, md: 10 }}
        paddingRight={{ base: 4, md: 10 }}
        height={headerHeight}
        css={{
          WebkitTransform: "translateZ(0)",
          transform: "translateZ(0)",
          backgroundRepeat,
          backgroundColor: color,
          backgroundPosition,
          backgroundSize,
          borderBottom,
          borderBottomStyle,
          borderBottomColor,
          backgroundOrigin: "border-box",
          _light: {
            backgroundImage:
              gradeTheme === GradeTheme.Primary ? "none" : backgroundImageLight
          },
          _dark: {
            backgroundImage:
              gradeTheme === GradeTheme.Primary ? "none" : backgroundImageDark
          }
        }}
      >
        <Flex gap={4} alignItems="center">
          <HStack gap="2">
            {showBreadcrumb ? (
              <LessonHeaderBreadcrumb textColor={textColor} />
            ) : (
              <>
                <Link
                  to={getHomeRoute(selectedGrade?.grade_code as number)}
                  aria-disabled={isOrientation}
                  tabIndex={isOrientation ? -1 : undefined}
                  onClick={
                    isOrientation
                      ? (e: MouseEvent<HTMLAnchorElement>) => e.preventDefault()
                      : undefined
                  }
                  style={isOrientation ? { pointerEvents: "none" } : undefined}
                  data-tracking-id={getTrackingId(
                    [CLS.Header, CLS.Link],
                    "logo-home"
                  )}
                >
                  <Logo
                    variant={isMobile ? "brandmark" : "inline"}
                    color="gradeTheme.headerLogo"
                    size={isMobile ? "md" : "xl"}
                    isDarkMint={false}
                  />
                </Link>

                {!isOrientation && (
                  <>
                    <Separator
                      className="print-hide"
                      orientation="vertical"
                      height={8}
                      size="sm"
                      borderColor={separatorColor}
                      ml="3"
                    />

                    {headerDetailsStatus !== REQUEST_STATUS.PENDING &&
                      headerDetails.grades.length > 0 && (
                        <Flex
                          className="print-hide"
                          alignItems="center"
                          padding="4px 8px"
                          ml="8px"
                          {...(headerDetails.grades.length > 1 && {
                            _hover: {
                              "backgroundColor": "button.bg.filledHover",
                              "cursor": "pointer",
                              "borderRadius": "4px",
                              "& *": {
                                color: "base.200"
                              }
                            }
                          })}
                          onClick={
                            headerDetails.grades.length > 1
                              ? () => gradeSelectModalRef.current?.show()
                              : undefined
                          }
                          data-tracking-id={getTrackingId(
                            [CLS.Header, CLS.Button],
                            "grade-selector"
                          )}
                        >
                          <Text
                            textStyle="title.3"
                            color={textColor}
                            data-testid="selected-grade-label"
                          >
                            {selectedGrade?.name}
                          </Text>
                          {headerDetails.grades.length > 1 && (
                            <Menu.Root
                              positioning={{ placement: "right-start" }}
                              closeOnSelect={false}
                            >
                              <Menu.Trigger asChild>
                                <NavigationIcons.Dots
                                  size="lg"
                                  color={iconColor}
                                  cursor="pointer"
                                />
                              </Menu.Trigger>
                            </Menu.Root>
                          )}
                        </Flex>
                      )}
                  </>
                )}
              </>
            )}
          </HStack>
        </Flex>

        <Flex gap={3} alignItems="center" className="print-hide">
          <Link
            to={getAccountRoute(selectedGrade?.grade_code as number)}
            onClick={
              isOrientation
                ? (e: MouseEvent<HTMLAnchorElement>) => e.preventDefault()
                : handleAccountClick
            }
            aria-disabled={isOrientation}
            tabIndex={isOrientation ? -1 : undefined}
            style={isOrientation ? { pointerEvents: "none" } : undefined}
            data-testid="header-account-link"
            data-tracking-id={getTrackingId(
              [CLS.Header, CLS.Link],
              "account-link"
            )}
          >
            <Flex gap={4} alignItems="center">
              <Text
                textStyle="body.large"
                fontWeight="semibold"
                display={{ base: "none", md: "block" }}
                color={textColor}
              >
                {headerDetails.student?.first_name}
              </Text>
              <AvatarImage
                size="xl"
                displayName={headerDetails.student?.first_name}
                profileImageSrc={getStudentLogo(
                  headerDetails.student?.avatar_key ?? 0
                )}
              />
            </Flex>
          </Link>

          {/* theme switch */}
          <ColorModeButton
            darkModeIcon={
              <NavigationIcons.Moonlight
                color="gradeTheme.colorModeIcon"
                boxSize="12"
              />
            }
            lightModeIcon={
              <NavigationIcons.Sunlight
                color="gradeTheme.colorModeIcon"
                boxSize="12"
              />
            }
            data-tracking-id={getTrackingId(
              [CLS.Header, CLS.Button],
              "color-mode-toggle"
            )}
          />
        </Flex>
      </Flex>
      {gradeTheme === GradeTheme.Primary && (
        <Flex
          className="header-decorative-wave"
          w="100%"
          h={{ base: "12px", md: "16px" }}
          justify="center"
          alignItems="center"
          bgColor="transparent"
          css={{
            WebkitTransform: "translateZ(0)",
            transform: "translateZ(0)",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "bottom",
            backgroundSize: "100% 100%",
            backgroundOrigin: "border-box",
            _light: {
              backgroundImage: backgroundImageLight
            },
            _dark: {
              backgroundImage: backgroundImageDark
            }
          }}
        />
      )}

      <SelectGradeModal
        modalRef={gradeSelectModalRef}
        grades={headerDetails.grades}
        handleGradeSelect={selectedNewGrade =>
          handleGradeSelect(selectedNewGrade)
        }
        errorMessage={getOTPError}
        handleGradeChangeCancel={handleGradeChangeCancel}
        isOtpLoading={getGradeChangeOtpRequestStatus === REQUEST_STATUS.PENDING}
        isProgrammaticCloseRef={closingForOtpRef}
      />

      <PinInputModal
        modalRef={otpModalRef}
        pinCodes={pinCodes}
        onPinVerification={onPinVerification}
        handleRequestNewCode={handleRequestNewCode}
        isRequestNewCode={
          getGradeChangeOtpRequestStatus === REQUEST_STATUS.PENDING
        }
        handleInputChange={setPinCodes}
        isSubmitOTP={
          validateGradeChangeOtpRequestStatus === REQUEST_STATUS.PENDING
        }
        errorMessage={validateOTPError}
        handlePinCancel={handlePinCancel}
        title={`Start ${targetGrade?.name}`}
        descriptionLineOne="We need to make sure parents know when new grades commence because they may be required to provide enrolment details."
        descriptionLineTwo={`To start a ${targetGrade?.name}, enter the pin code we sent to your parent’s email address.`}
        isProgrammaticCloseRef={closingForValidateOtpRef}
      />
    </Flex>
  );
};

export default Header;
